import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';

function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s}
          type="button"
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(s)}
          className="focus:outline-none"
        >
          <Star className={`w-7 h-7 transition-colors ${s <= (hovered || value) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-100'}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div className="border-b border-gray-100 pb-4">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-full bg-cs-blue text-white text-xs font-bold flex items-center justify-center">
          {review.author_name?.[0]?.toUpperCase()}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-800">{review.author_name}</p>
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(s => (
              <Star key={s} className={`w-3 h-3 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`} />
            ))}
            {review.verified_purchase && (
              <span className="text-[10px] text-green-600 font-medium ml-1">✓ Achat vérifié</span>
            )}
          </div>
        </div>
        <span className="ml-auto text-xs text-gray-400">
          {new Date(review.created_date).toLocaleDateString('fr-FR')}
        </span>
      </div>
      {review.comment && <p className="text-sm text-gray-600 mt-1 leading-relaxed">{review.comment}</p>}
    </div>
  );
}

export default function ProductReviews({ productId, productName }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ author_name: '', rating: 0, comment: '' });
  const [submitted, setSubmitted] = useState(false);

  const { data: reviews = [] } = useQuery({
    queryKey: ['reviews', productId],
    queryFn: () => base44.entities.Review.filter({ product_id: productId, approved: true }, '-created_date', 50),
    enabled: !!productId,
  });

  const mutation = useMutation({
    mutationFn: (data) => base44.entities.Review.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', productId] });
      setForm({ author_name: '', rating: 0, comment: '' });
      setShowForm(false);
      setSubmitted(true);
    },
  });


  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.rating || !form.author_name.trim()) return;
    mutation.mutate({ ...form, product_id: productId, product_name: productName, approved: false });
  };

  return (
    <div className="mt-16 pt-10 border-t border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">AVIS CLIENTS</h2>
          {avgRating && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`w-4 h-4 ${s <= Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`} />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-700">{avgRating}/5</span>
              <span className="text-sm text-gray-400">({reviews.length} avis)</span>
            </div>
          )}
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setSubmitted(false); }}
            className="bg-cs-blue text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-cs-blue-dark transition-colors"
          >
            Laisser un avis
          </button>
        )}
      </div>

      {/* Success message */}
      {submitted && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">
          ✓ Merci pour votre avis ! Il sera publié après validation par notre équipe.
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-4">Votre avis</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Votre note *</label>
              <StarPicker value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Votre prénom *</label>
              <input
                required
                value={form.author_name}
                onChange={e => setForm(f => ({ ...f, author_name: e.target.value }))}
                placeholder="Marie D."
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-cs-blue"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Votre commentaire</label>
              <textarea
                value={form.comment}
                onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
                rows={3}
                placeholder="Partagez votre expérience..."
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-cs-blue resize-none"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={!form.rating || mutation.isPending}
              className="px-5 py-2 bg-cs-blue text-white text-sm font-bold rounded hover:bg-cs-blue-dark transition-colors disabled:opacity-50">
              {mutation.isPending ? 'Envoi...' : 'Publier'}
            </button>
          </div>
        </form>
      )}

      {/* Reviews list */}
      {reviews.length === 0 && !showForm ? (
        <p className="text-sm text-gray-400 text-center py-8">Aucun avis pour l'instant — soyez le premier !</p>
      ) : (
        <div className="space-y-4">
          {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
        </div>
      )}
    </div>
  );
}